import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../supabase';

// VAPID public key — safe to hardcode, this is a public credential by design
const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC_KEY ||
  'BPW0exaG2fj-BYYf1c4Hw3FKKEMRekrvBnzTSGKqdgcDfr3Ae1WfROmiOjDPWmgBmcmWc4Z8QyZ-izIqgeC7QmY';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const saveTokenToSupabase = async (token) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
    const { error } = await supabase.from('push_subscriptions').upsert({
      user_id: user.id,
      endpoint: token,         // reuse endpoint column for native device token
      p256dh: 'native',        // marker so backend knows it's a native token
      auth: Capacitor.getPlatform(), // 'ios' or 'android'
      timezone,
    }, { onConflict: 'user_id,endpoint' });
    return !error;
  } catch {
    return false;
  }
};

// ─── Native (Capacitor) push notifications ───────────────────────────────────
const useNativePush = () => {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    PushNotifications.addListener('registration', async (token) => {
      const saved = await saveTokenToSupabase(token.value);
      if (saved) setIsSubscribed(true);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[Push] Received:', notification);
    });

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, []);

  const requestPermissionAndSubscribe = async () => {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const result = await PushNotifications.requestPermissions();
      if (result.receive === 'granted') {
        setPermission('granted');
        await PushNotifications.register();
        return true;
      } else {
        setPermission('denied');
        return false;
      }
    } catch (err) {
      console.error('[Push] Native subscribe failed:', err);
      return false;
    }
  };

  const unsubscribe = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_id', user.id)
          .eq('auth', Capacitor.getPlatform());
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error('[Push] Native unsubscribe failed:', err);
    }
  };

  return {
    isSupported: true,
    permission,
    isSubscribed,
    subscribe: requestPermissionAndSubscribe,
    unsubscribe,
  };
};

// ─── Web (Service Worker) push notifications ─────────────────────────────────
const useWebPush = () => {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported] = useState(
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );

  useEffect(() => {
    if (!isSupported) return;
    checkSubscription();
  }, [isSupported]);

  const checkSubscription = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {
      // silent
    }
  };

  const subscribe = async () => {
    if (!isSupported) return false;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const { p256dh, auth } = sub.toJSON().keys;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
      const { error } = await supabase.from('push_subscriptions').upsert({
        user_id: user.id,
        endpoint: sub.endpoint,
        p256dh,
        auth,
        timezone,
      }, { onConflict: 'user_id,endpoint' });

      if (error) throw error;
      setPermission('granted');
      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('[Push] Web subscribe failed:', err?.message || err);
      return false;
    }
  };

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', user.id)
            .eq('endpoint', sub.endpoint);
        }
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error('[Push] Web unsubscribe failed:', err);
    }
  };

  const requestPermissionAndSubscribe = async () => {
    if (!isSupported) return false;
    if (Notification.permission === 'denied') return false;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === 'granted') return subscribe();
    return false;
  };

  return {
    isSupported,
    permission,
    isSubscribed,
    subscribe: requestPermissionAndSubscribe,
    unsubscribe,
  };
};

// ─── Unified hook — picks native or web automatically ────────────────────────
export const usePushNotifications = () => {
  const isNative = Capacitor.isNativePlatform();
  const native = useNativePush();
  const web = useWebPush();
  return isNative ? native : web;
};
